import { Outlet, useEffect, useLocation, useNavigate, useState } from "@modica-library/react19";
import { ModicaBreadcrumb } from "@modica-library/breadcrumb/react";
import { Chip } from "../ui";
import eziLogo from "../assets/ezilogo.svg";
import { ModicaFooter } from "../brand/ModicaFooter";
import { ProgressGuide } from "../components/ProgressGuide";
import { ModicaIcon } from "@modica-library/icons/react";
import { getSession } from "../lib/api";
import { readDemoSession } from "../lib/session";
import "./Dashboard.css";

const SIDEBAR_STATIC = ["OTHER MESSAGING", "INTEGRATIONS", "TOOLS", "DATA & INSIGHTS", "ACCOUNT"];

const PAGE_LABELS: Record<string, string> = {
  "/dashboard/send": "Send a Message",
  "/dashboard/inbox": "Inbox",
};

// Items with a route navigate; the rest are placeholders until those flows exist.
const MENU_ITEMS: { label: string; to?: string }[] = [
  { label: "New Message", to: "/dashboard/send" },
  { label: "Inbox", to: "/dashboard/inbox" },
  { label: "Contacts" },
  { label: "Groups" },
  { label: "Templates" },
  { label: "Summary", to: "/dashboard" },
  { label: "Logs" },
  { label: "Administration" },
  { label: "Top Up Credits" },
];

/** Shared demo shell: top nav, sidebar, footer, and the always-present
 *  ProgressGuide. Pages render into the grid via <Outlet/>; because the guide
 *  lives here (not per page), its open/collapsed state survives navigation. */
export function DashboardLayout() {
  const [session, setSession] = useState(readDemoSession);
  const [navOpen, setNavOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(true);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Session guard: the embedded web2sms components hard-navigate to "/" when
  // an API call 401s (their session-expired behaviour), which reads as an app
  // reload. Validate the cookie on every route change instead and bounce to
  // /verify cleanly; also refreshes credits/sendsUsed shown in the subbar.
  useEffect(() => {
    let cancelled = false;
    getSession()
      .then((fresh) => {
        if (cancelled) return;
        sessionStorage.setItem("demoSession", JSON.stringify(fresh));
        setSession(fresh);
      })
      .catch(() => {
        if (!cancelled) navigate("/verify?expired=1", { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [pathname, navigate]);

  return (
    <div className="dashboard">
      <header className="topnav">
        <div className="topnav__left">
          <button
            className="topnav__menu"
            onClick={() => setNavOpen((o) => !o)}
            aria-label={navOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={navOpen}
          >
            <ModicaIcon iconName={navOpen ? "close" : "menu"} size="medium" decorative color="white" />
          </button>
          <button
            type="button"
            className="topnav__logo"
            onClick={() => navigate("/dashboard")}
            aria-label="Go to dashboard home"
          >
            <img src={eziLogo} alt="EziTxt" height={40} />
          </button>
        </div>
        <nav className="topnav__links">
          <a href="#">Website</a>
          <a href="#" className="topnav__help">
            <ModicaIcon iconName="help" color="white" decorative />
            Help Docs
          </a>
        </nav>
      </header>

      <div className="subbar">
        <div className="subbar__crumbs">
          <ModicaBreadcrumb
            ariaLabel="You are here"
            preventDefault
            items={
              pathname === "/dashboard"
                ? [{ label: "Dashboard" }]
                : [{ label: "Dashboard", href: "/dashboard" }, { label: PAGE_LABELS[pathname] ?? "Page" }]
            }
            onClick={(e: CustomEvent) => {
              const href = (e.detail as { href?: string } | undefined)?.href;
              if (href) navigate(href);
            }}
          />
        </div>
        <div className="subbar__right">
          <span className="subbar__demo">
            Demo for <strong>{session.email}</strong>
          </span>
          <Chip tone="coin">{session.credits} TEST SMS</Chip>
        </div>
      </div>

      <div className="dashboard__grid">
        <aside className={`sidebar${navOpen ? " is-mobile-open" : ""}`}>
          <button
            type="button"
            className={`sidebar__item sidebar__item--toggle${menuOpen ? " is-open" : ""}`}
            onClick={() => setMenuOpen((o) => !o)}
            aria-expanded={menuOpen}
          >
            <span>EZIMESSAGE</span>
            <span className="sidebar__caret">
              <ModicaIcon iconName="keyboard_arrow_down" color={menuOpen ? "white" : "black"} decorative />
            </span>
          </button>
          <div className={`sidebar__sub${menuOpen ? " is-open" : ""}`}>
            <div className="sidebar__sub-inner">
              {MENU_ITEMS.map((item) => {
                const isActive = item.to != null && pathname === item.to;
                return (
                  <button
                    key={item.label}
                    type="button"
                    className={`sidebar__subitem${isActive ? " is-active" : ""}`}
                    onClick={() => {
                      if (!item.to) return;
                      navigate(item.to);
                      setNavOpen(false);
                    }}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
          {SIDEBAR_STATIC.map((s) => (
            <div key={s} className="sidebar__item">
              <span>{s}</span>
              <span className="sidebar__caret">
                <ModicaIcon iconName="keyboard_arrow_down" color="black" decorative />
              </span>
            </div>
          ))}
        </aside>

        <Outlet />
      </div>

      <footer className="dashboard__footer">
        <ModicaFooter />
      </footer>

      <ProgressGuide />
    </div>
  );
}
